import "./tender-modal.css";

import { useContext, useState } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";

import type { ConfirmationScreenVariant } from "./components/confirmation-screen/confirmation-screen.tsx";
import { ConfirmationScreen } from "./components/confirmation-screen/confirmation-screen.tsx";
import { SendDocsScreen } from "./components/send-docs-screen/send-docs-screen.tsx";
import { defaultLabelSize } from "./constants/tender-modal-constants.ts";
import type {
	SendDocsFormData,
	TenderFormData,
} from "./hooks/use-tender-modal.tsx";
import {
	useGenerateReports,
	useSendCustomerDocs,
	useSendCustomerDocsWithData,
	useSendDocsWithData,
	useShippingLabelReport,
} from "./hooks/use-tender-modal.tsx";
import { downloadPdf, openPdfInNewTab } from "./utils/pdf-utils.ts";

type ModalProps = {
	showModal: boolean;
	onToggleModal: () => void;
	title?: string;
	/**
	 * "updated" and "documents" show `title` in the modal's title bar (on the
	 * confirmation screen); "tendered" keeps the centered header.
	 */
	variant?: ConfirmationScreenVariant;
	/**
	 * When provided, BOL/label generation failures call this instead of showing
	 * error toasts.
	 */
	onGenerateError?: () => void;
};

const TITLE_BAR_CLASS_NAMES: Partial<
	Record<ConfirmationScreenVariant, string>
> = {
	updated: "tender-modal-updated-dialog",
	documents: "tender-modal-documents-dialog",
};

export const TenderModal = ({
	showModal,
	onToggleModal,
	title,
	variant = "tendered",
	onGenerateError,
}: ModalProps) => {
	const { isInternal } = useDeploymentType();
	// External docs-email recipients UI is CID-gated behind this LD flag. Disabled
	// CIDs keep the send-only behavior; enabled CIDs get the full compose screen
	// with editable recipients and read-only subject/body.
	const { isReady: isRecipientsFlagReady, isEnabled: isRecipientsFlagEnabled } =
		useFeatureFlagGate("traxLtlPostTenderEmailRecipients");
	const { isPending: isShippingLabelLoading } = useShippingLabelReport();
	const { generateReports, isBolFetching, isGeneratingReports } =
		useGenerateReports({ onError: onGenerateError });
	const [showSendDocsScreen, setShowSendDocsScreen] = useState(false);
	const { quoteState } = useContext(QuoteDetailsContext);
	const [labelFormData, setLabelFormData] = useState<TenderFormData>({
		labelSize: defaultLabelSize,
		labelQuantity: quoteState.items.reduce(
			(acc, curr) => acc + (curr.quantity ?? 0),
			0,
		),
		additionalDetails: "",
	});

	// State for send docs form data - initialized with null and set on first render
	const [sendDocsFormData, setSendDocsFormData] =
		useState<SendDocsFormData | null>(null);

	// Get the send docs hook - this must be at the top level
	const sendDocsHook = useSendDocsWithData(labelFormData);
	const sendCustomerDocsWithDataHook =
		useSendCustomerDocsWithData(labelFormData);
	const sendCustomerDocsHook = useSendCustomerDocs();

	// External users on flag-enabled CIDs use the same compose screen as internal
	// users (read-only subject/body, editable recipients). Everyone else keeps
	// the direct send-only behavior.
	const showComposeScreen =
		isInternal || (isRecipientsFlagReady && isRecipientsFlagEnabled);
	const composeDocsHook = isInternal
		? sendDocsHook
		: sendCustomerDocsWithDataHook;

	const isLoading =
		isShippingLabelLoading ||
		isBolFetching ||
		isGeneratingReports ||
		sendDocsHook.isLoading ||
		sendCustomerDocsWithDataHook.isLoading ||
		sendCustomerDocsHook.isLoading;

	const handleView = (formData: TenderFormData) => {
		generateReports(formData, openPdfInNewTab).catch(console.error);
	};

	const handleDownload = (formData: TenderFormData) => {
		generateReports(formData, (pdf, filename) => {
			downloadPdf(pdf, filename);
		}).catch(console.error);
	};

	const handleSendClick = (formData: TenderFormData) => {
		if (showComposeScreen) {
			setLabelFormData(formData);
			setShowSendDocsScreen(true);
		} else {
			sendCustomerDocsHook.sendDocs(formData);
		}
	};

	// Handlers for send docs screen (uses stored form data)
	const handleViewFromSendDocs = () => {
		handleView(labelFormData);
	};

	const handleDownloadFromSendDocs = () => {
		handleDownload(labelFormData);
	};

	if (!showModal) return null;

	// The send docs screen has its own header, so the title bar is only shown
	// over the confirmation screen.
	const showTitleBar = variant !== "tendered" && !showSendDocsScreen;

	return (
		<TraxModal
			className={showTitleBar ? TITLE_BAR_CLASS_NAMES[variant] : undefined}
			onClose={() => onToggleModal()}
			title={showTitleBar ? title : undefined}
		>
			{isLoading ? <LoadingSpinner /> : null}
			{showSendDocsScreen ? (
				<SendDocsScreen
					formData={sendDocsFormData}
					isLoading={isLoading}
					onBackClick={() => setShowSendDocsScreen(false)}
					onDownload={handleDownloadFromSendDocs}
					onFormDataChange={setSendDocsFormData}
					onView={handleViewFromSendDocs}
					readOnlyContent={!isInternal}
					sendDocsHook={composeDocsHook}
				/>
			) : (
				<ConfirmationScreen
					formData={labelFormData}
					isLoading={isLoading}
					onDownload={handleDownload}
					onSendClick={handleSendClick}
					onView={handleView}
					title={title}
					variant={variant}
				/>
			)}
		</TraxModal>
	);
};
